import React, { RefObject, useEffect, useState } from 'react'
import { Box, Button, IconButton, PopoverActions, Tooltip, Typography } from '@mui/material'
import { UseFormRegister, UseFormResetField, UseFormWatch } from 'react-hook-form'
import { NewTechnologyFormValues } from '../../store/types'
import { Clear, Close, ImageSearch } from '@mui/icons-material'
import { useTranslation } from 'react-i18next'
import { useGetTechnologyExtensionsQuery } from '../../store/redux/reducers/technologiesSlice'
import { useNotification } from '../../store/redux/hooks'
import { environment } from '../../enviroments/enviroment'

type ImageUploadButtonProps = {
  name: keyof NewTechnologyFormValues
  label: string
  register: UseFormRegister<NewTechnologyFormValues>
  watch: UseFormWatch<NewTechnologyFormValues>
  resetField: UseFormResetField<NewTechnologyFormValues>
  clearImage: boolean
  setClearImage: React.Dispatch<React.SetStateAction<boolean>>
  popoverRef: RefObject<PopoverActions>
}

const ImageUploadButton = ({
  name,
  label,
  register,
  watch,
  resetField,
  clearImage,
  setClearImage,
  popoverRef
}: ImageUploadButtonProps) => {
  const { t } = useTranslation()
  const { displayNotification } = useNotification()

  const handleClearExisting = () => {
    resetField(name, { defaultValue: '' })
    setClearImage(false)
  }

  const { data: allowedTypes } = useGetTechnologyExtensionsQuery({})
  const [message, setMessage] = useState(t('components.button.imageUpload.fileNotSelected'))
  const [clearButtonVisible, setClearButtonVisible] = useState(false)

  const handleClear = () => {
    resetField(name)
  }

  const MAX_TECH_ICON_SIZE_IN_MB = (environment.maxTechIconSizeMb as unknown as number) || 0.02

  useEffect(() => {
    const subscription = watch(value => {
      if (clearImage) return
      let msg = t('components.button.imageUpload.fileNotSelected')

      setClearButtonVisible(false)
      setMessage(msg)

      if (!value.logo || !value.logo[0]) return

      const fileSizeInMB = (value.logo[0] as File).size / (1024 * 1024)

      if (fileSizeInMB > MAX_TECH_ICON_SIZE_IN_MB) {
        displayNotification({
          message: t('fileSizeError', { fileSize: MAX_TECH_ICON_SIZE_IN_MB * 1024 }),
          type: 'error',
        })
        return
      }

      setClearButtonVisible(true)
      setMessage(t('components.button.imageUpload.selected') + (value.logo[0] as File).name)
    })

    return () => {
      subscription.unsubscribe()
    }
  }, [clearImage, t, watch, displayNotification, MAX_TECH_ICON_SIZE_IN_MB])

  useEffect(() => {
    popoverRef.current?.updatePosition()
  }, [popoverRef, message])

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1,
        alignItems: 'center',
        width: '100%',
        justifyContent: clearImage ? 'center' : 'flex-start',
      }}
    >
      {clearImage ? (
        <Button
          type="button"
          onClick={handleClearExisting}
          {...register(name)}
          variant="contained"
          color="secondary"
          startIcon={<Clear sx={{ width: 14, height: 14 }} />}
          sx={{ width: '100%' }}
          aria-label={t('page.adminsPage.edit.clear')}
        >
          {t('page.adminsPage.edit.clear')}
        </Button>
      ) : (
        <>
          <Tooltip title={t('components.tooltip.imageUpload')}>
            <Button
              component="label"
              variant="contained"
              color="secondary"
              htmlFor={name}
              aria-label={label}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  document.getElementById(name)?.click()
                }
              }}
            >
              <ImageSearch />
              <input
                type="file"
                accept={allowedTypes !== undefined ? allowedTypes.join(', ') : ''}
                id={name}
                style={{ display: 'none' }}
                {...register(name)}
              />
            </Button>
          </Tooltip>

          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
            <Box
              component="div"
              sx={{
                width: '100%',
                maxWidth: '150px',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
                textOverflow: 'ellipsis',
              }}
            >
              <Typography variant="caption">{message}</Typography>
            </Box>
            {clearButtonVisible && (
              <Tooltip title={t('page.adminsPage.edit.clear')}>
                <IconButton size="small" onClick={handleClear} aria-label={t('page.adminsPage.edit.clear')}>
                  <Close sx={{ width: 12, height: 12 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        </>
      )}
    </Box>
  )
}

export default ImageUploadButton
